import { createContext, ReactNode, RefObject, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HomeHero } from './HomeHero';
import { HomeHeroScene, homeHeroSceneContext } from './homeHeroScene';

type PersistentHomeHeroContext = { heroNode: HTMLDivElement; parkingRef: RefObject<HTMLDivElement> } | null;

const persistentHomeHeroContext = createContext<PersistentHomeHeroContext>(null);

export const PersistentHomeHero = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => {
  const [heroNode] = useState(() => Object.assign(document.createElement('div'), { className: 'h-full w-full' }));
  const parkingRef = useRef<HTMLDivElement>(null);
  const contextValue = useMemo(() => ({ heroNode, parkingRef }), [heroNode]);
  const [scene, setScene] = useState<HomeHeroScene>();

  useLayoutEffect(() => {
    if (enabled && !heroNode.isConnected) {
      parkingRef.current?.appendChild(heroNode);
    }
  }, [enabled, heroNode]);

  return (
    <persistentHomeHeroContext.Provider value={enabled ? contextValue : null}>
      <homeHeroSceneContext.Provider value={setScene}>{children}</homeHeroSceneContext.Provider>
      {enabled && (
        <>
          <div
            ref={parkingRef}
            aria-hidden
            className="pointer-events-none invisible fixed top-0 -left-[200vw] h-[calc(605px+env(safe-area-inset-top))] w-screen max-w-[510px] overflow-hidden"
          />
          {createPortal(<HomeHero scene={scene} />, heroNode)}
        </>
      )}
    </persistentHomeHeroContext.Provider>
  );
};

export const HomeHeroSlot = () => {
  const persistentHomeHero = useContext(persistentHomeHeroContext);
  const slotRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!persistentHomeHero || !slotRef.current) {
      return;
    }
    const { heroNode, parkingRef } = persistentHomeHero;
    slotRef.current.appendChild(heroNode);
    return () => {
      parkingRef.current?.appendChild(heroNode);
    };
  }, [persistentHomeHero]);

  return (
    <div
      ref={slotRef}
      data-testid="home-hero"
      className="h-[calc(605px+env(safe-area-inset-top))] w-full overflow-hidden rounded-b-28"
    />
  );
};
